/**
 * Enhanced HOD Dashboard Component
 * Category-specific dashboard for Head of Department users
 * Features pending request approval, worker assignment, and team management
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CardActionArea,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  AssignmentInd as AssignmentIndIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { hodAPI } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import Layout from "../common/Layout";

const HODDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const { sendNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Dashboard data
  const [hodProfile, setHodProfile] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    pendingReview: 0,
    autoApproved: 0,
    assigned: 0,
    inProgress: 0,
    resolved: 0,
    totalRequests: 0,
    teamMembers: 0,
    avgResolutionTime: 0,
  });

  // Requests data
  const [pendingRequests, setPendingRequests] = useState([]);
  const [assignedRequests, setAssignedRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [managedWorkers, setManagedWorkers] = useState([]);

  // UI state
  const [activeTab, setActiveTab] = useState(0);
  const [currentView, setCurrentView] = useState("dashboard"); // dashboard, pending, assigned, inprogress, team

  // Dialog states
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    request: null,
    decision: "",
    comments: "",
    priority: "",
    loading: false,
  });

  const [assignmentDialog, setAssignmentDialog] = useState({
    open: false,
    request: null,
    assignmentType: "internal", // "internal" or "external"
    workerId: "",
    // External worker fields
    externalWorkerName: "",
    externalWorkerPhone: "",
    externalWorkerEmail: "",
    externalWorkerType: "external", // "external", "temporary", "third_party"
    // Common fields
    scheduledDate: "",
    estimatedDuration: "",
    notes: "",
    loading: false,
  });

  // Delete worker dialog state
  const [deleteWorkerDialog, setDeleteWorkerDialog] = useState({
    open: false,
    worker: null,
    loading: false,
  });

  const [requestDetailDialog, setRequestDetailDialog] = useState({
    open: false,
    request: null,
  });

  // Load managed workers
  const loadManagedWorkers = useCallback(
    async (hodId) => {
      if (!hodId) {
        // If no hodId provided, get it from current HOD profile
        if (hodProfile?.id) {
          hodId = hodProfile.id;
        } else {
          console.warn("No HOD ID available for loading managed workers");
          console.log("[DEBUG] Current hodProfile:", hodProfile);
          return;
        }
      }

      try {
        console.log("[DEBUG] Loading managed workers for HOD ID:", hodId);
        console.log("[DEBUG] HOD ID type:", typeof hodId);
        console.log("[DEBUG] HOD ID value:", JSON.stringify(hodId));

        // Robust validation and cleaning of HOD ID
        let cleanHodId;
        if (typeof hodId === "string" && hodId.trim() !== "") {
          cleanHodId = hodId.trim();
        } else if (typeof hodId === "object" && hodId !== null) {
          // If it's an object, check if it has an id property
          if (hodId.id) {
            cleanHodId = String(hodId.id).trim();
          } else {
            throw new Error(
              `Invalid HOD ID: received object ${JSON.stringify(hodId)}`
            );
          }
        } else {
          cleanHodId = String(hodId);
        }

        console.log("[DEBUG] Cleaned HOD ID:", cleanHodId);

        if (
          !cleanHodId ||
          cleanHodId === "undefined" ||
          cleanHodId === "null"
        ) {
          throw new Error(`Invalid HOD ID after cleaning: "${cleanHodId}"`);
        }

        const response = await hodAPI.getManagedWorkers(cleanHodId);
        setManagedWorkers(response.data || []);
      } catch (err) {
        console.error("Error loading managed workers:", err);
        console.log(
          "[DEBUG] Failed API call URL would be:",
          `/api/v1/hods/${hodId}/workers`
        );
        setError(
          err.response?.data?.message ||
            `Failed to load team members: ${err.message}`
        );
      }
    },
    [hodProfile]
  );

  // Load HOD profile and dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      // Load HOD profile
      const profileResponse = await hodAPI.getMyProfile();
      console.log("[DEBUG] HOD Profile Response:", profileResponse.data);
      setHodProfile(profileResponse.data);

      // Load dashboard statistics
      const statsResponse = await hodAPI.getHODStats();
      setDashboardStats(statsResponse.data);

      // Load managed workers using HOD ID
      if (profileResponse.data?.id) {
        console.log(
          "[DEBUG] HOD ID for workers:",
          profileResponse.data.id,
          typeof profileResponse.data.id
        );
        loadManagedWorkers(profileResponse.data.id);
      }
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setError(err.response?.data?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [loadManagedWorkers]);

  // Load pending requests
  const loadPendingRequests = async () => {
    try {
      const response = await hodAPI.getPendingRequests();
      setPendingRequests(response.data || []);
    } catch (err) {
      console.error("Error loading pending requests:", err);
      setError(
        err.response?.data?.message || "Failed to load pending requests"
      );
    }
  };

  // Load assigned requests
  const loadAssignedRequests = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      setLoading(true);
      const response = await hodAPI.getAssignedRequests();
      setAssignedRequests(response.data || []);
    } catch (err) {
      console.error("Error loading assigned requests:", err);
      setError("Failed to load assigned requests");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load all requests
  const loadAllRequests = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      console.log("[DEBUG] Loading all requests for HOD...");
      setLoading(true);
      const response = await hodAPI.getAllCategoryRequests();
      console.log(
        "[DEBUG] All requests loaded:",
        response.data?.length || 0,
        "requests"
      );
      setAllRequests(response.data || []);
    } catch (err) {
      console.error("Error loading all requests:", err);
      console.error("Error details:", err.response?.data);
      setError("Failed to load all requests");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Centralized refresh function for all CRUD operations
  const refreshAllData = async () => {
    try {
      console.log("[DEBUG] Refreshing all HOD dashboard data...");
      await Promise.all([
        loadPendingRequests(),
        loadAssignedRequests(),
        loadAllRequests(),
        loadDashboardData(),
      ]);
      console.log(
        "[DEBUG] All data refreshed - cards should show latest activity"
      );
    } catch (error) {
      console.error("[ERROR] Failed to refresh data:", error);
    }
  };

  // Delete worker function
  const handleDeleteWorker = async () => {
    if (!deleteWorkerDialog.worker) return;

    setDeleteWorkerDialog((prev) => ({ ...prev, loading: true }));

    try {
      console.log("[DEBUG] Deleting worker:", deleteWorkerDialog.worker.id);

      const response = await hodAPI.deleteWorker(deleteWorkerDialog.worker.id);

      if (response.success) {
        setSuccess(
          `Worker ${deleteWorkerDialog.worker.name} deleted successfully`
        );

        // Close dialog
        setDeleteWorkerDialog({
          open: false,
          worker: null,
          loading: false,
        });

        // Refresh data to show updated team
        await refreshAllData();

        // Refresh managed workers specifically
        if (hodProfile?.id) {
          loadManagedWorkers(hodProfile.id);
        }
      } else {
        throw new Error(response.message || "Failed to delete worker");
      }
    } catch (err) {
      console.error("Error deleting worker:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to delete worker"
      );
      setDeleteWorkerDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // Initial load
  useEffect(() => {
    if (currentUser?.uid) {
      loadDashboardData();
      loadPendingRequests();
      loadAssignedRequests();
      loadAllRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]); // Only depend on currentUser.uid to prevent constant refreshes

  // Handle request review (approve/reject)
  const handleRequestReview = async () => {
    try {
      setReviewDialog((prev) => ({ ...prev, loading: true }));

      await hodAPI.reviewRequest(reviewDialog.request.id, {
        decision: reviewDialog.decision,
        hodComments: reviewDialog.comments,
        estimatedPriority: reviewDialog.priority,
      });

      setSuccess(`Request ${reviewDialog.decision} successfully`);
      setReviewDialog({
        open: false,
        request: null,
        decision: "",
        comments: "",
        priority: "",
        loading: false,
      });

      // Refresh all data to show latest updates in cards
      await refreshAllData();
    } catch (err) {
      console.error("Error reviewing request:", err);
      setError(err.response?.data?.message || "Failed to review request");
      setReviewDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // Handle worker assignment
  const handleWorkerAssignment = async () => {
    try {
      setAssignmentDialog((prev) => ({ ...prev, loading: true }));

      if (assignmentDialog.assignmentType === "internal") {
        // Assign internal worker
        await hodAPI.assignWorker(assignmentDialog.request.id, {
          workerId: assignmentDialog.workerId,
          scheduledDate: assignmentDialog.scheduledDate,
          estimatedDuration: parseInt(assignmentDialog.estimatedDuration),
          hodNotes: assignmentDialog.notes,
        });
      } else {
        // Assign external worker
        const requestData = {
          workerName: assignmentDialog.externalWorkerName,
          workerPhone: assignmentDialog.externalWorkerPhone || null,
          workerEmail: assignmentDialog.externalWorkerEmail || null,
          workerType: assignmentDialog.externalWorkerType,
          scheduledDate: assignmentDialog.scheduledDate
            ? new Date(assignmentDialog.scheduledDate).toISOString()
            : null,
          estimatedDuration: assignmentDialog.estimatedDuration
            ? parseInt(assignmentDialog.estimatedDuration)
            : null,
          hodNotes: assignmentDialog.notes || null,
        };

        console.log("[DEBUG] Sending external worker assignment:", requestData);
        await hodAPI.assignExternalWorker(
          assignmentDialog.request.id,
          requestData
        );
      }

      setSuccess(
        `${
          assignmentDialog.assignmentType === "internal"
            ? "Internal"
            : "External"
        } worker assigned successfully`
      );
      setAssignmentDialog({
        open: false,
        request: null,
        assignmentType: "internal",
        workerId: "",
        externalWorkerName: "",
        externalWorkerPhone: "",
        externalWorkerEmail: "",
        externalWorkerType: "external",
        scheduledDate: "",
        estimatedDuration: "",
        notes: "",
        loading: false,
      });

      // Refresh all data to show latest updates in cards
      await refreshAllData();
    } catch (err) {
      console.error("Error assigning worker:", err);
      setError(err.response?.data?.message || "Failed to assign worker");
      setAssignmentDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // Navigation functions
  const navigateToPendingView = () => {
    setCurrentView("pending");
    setActiveTab(0);
    loadPendingRequests();
  };

  const navigateToAssignedView = () => {
    setCurrentView("assigned");
    setActiveTab(1);
    loadAssignedRequests();
  };

  const navigateToInProgressView = () => {
    setCurrentView("inprogress");
    setActiveTab(2);
    loadAllRequests();
  };

  const navigateToTeamView = () => {
    setCurrentView("team");
    setActiveTab(3);
    if (hodProfile?.id) {
      loadManagedWorkers(hodProfile.id);
    }
  };

  const navigateToDashboard = () => {
    setCurrentView("dashboard");
    setActiveTab(0);
  };

  // Open review dialog
  const openReviewDialog = (request) => {
    setReviewDialog({
      open: true,
      request,
      decision: "",
      comments: "",
      priority: request.priority || "medium",
      loading: false,
    });
  };

  // Open assignment dialog
  const openAssignmentDialog = (request) => {
    setAssignmentDialog({
      open: true,
      request,
      assignmentType: "internal",
      workerId: "",
      externalWorkerName: "",
      externalWorkerPhone: "",
      externalWorkerEmail: "",
      externalWorkerType: "external",
      scheduledDate: "",
      estimatedDuration: "",
      notes: "",
      loading: false,
    });
  };

  // Open request detail dialog
  const openRequestDetailDialog = (request) => {
    setRequestDetailDialog({
      open: true,
      request,
    });
  };

  // Smart request handler - opens appropriate dialog based on status and context
  const handleRequestAction = (request, fromView = null) => {
    // If request has pending HOD review and clicked from All Requests,
    // directly open assignment dialog for faster workflow
    if (request.status === "pending_hod_review" && fromView === "allRequests") {
      console.log(
        "[DEBUG] Opening assignment dialog for pending review request:",
        request.tokenNumber
      );
      openAssignmentDialog(request);
    } else {
      // Otherwise open the detail dialog
      openRequestDetailDialog(request);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
      case "pending_hod_review":
        return "warning";
      case "approved":
        return "success";
      case "assigned":
        return "info";
      case "in_progress":
        return "primary";
      case "resolved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading && !hodProfile) {
    return (
      <Layout title="HOD Dashboard">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout
      title={`HOD Dashboard - ${hodProfile?.category || "Category"} Department`}
    >
      <Box sx={{ width: "100%" }}>
        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* HOD Profile Header */}
        {hodProfile && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <SupervisorAccountIcon
                    sx={{ fontSize: 48, color: "primary.main" }}
                  />
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" gutterBottom>
                    {hodProfile.name}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Head of Department - {hodProfile.category} Category
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {hodProfile.email} | {hodProfile.experienceYears} years
                    experience
                  </Typography>
                </Grid>
                <Grid item>
                  <Chip
                    icon={<CategoryIcon />}
                    label={hodProfile.category}
                    color="primary"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Dashboard Statistics */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Pending Review Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardActionArea onClick={navigateToPendingView}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <WarningIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="warning.main">
                        {dashboardStats.pendingReview}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending Review
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Assigned Tasks Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardActionArea onClick={navigateToAssignedView}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <AssignmentIcon color="info" sx={{ mr: 2, fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="info.main">
                        {dashboardStats.assigned}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Assigned Tasks
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* In Progress Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardActionArea onClick={navigateToInProgressView}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TrendingUpIcon
                      color="primary"
                      sx={{ mr: 2, fontSize: 40 }}
                    />
                    <Box>
                      <Typography variant="h4" color="primary.main">
                        {dashboardStats.inProgress}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        In Progress
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Team Members Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardActionArea onClick={navigateToTeamView}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PersonIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {dashboardStats.teamMembers}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Team Members
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Tabs */}
        <Card>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={
                <Badge badgeContent={pendingRequests.length} color="warning">
                  Pending Requests
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={assignedRequests.length} color="info">
                  Assigned Tasks
                </Badge>
              }
            />
            <Tab label="All Requests" />
            <Tab label="Team Management" />
          </Tabs>

          {/* Pending Requests Tab */}
          {activeTab === 0 && (
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  Requests Awaiting Your Review ({pendingRequests.length})
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={refreshAllData}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>

              {pendingRequests.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <CheckCircleIcon
                    sx={{ fontSize: 64, color: "success.light", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    No pending requests
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All requests in your category are up to date!
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Token #</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Requester</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingRequests.map((request) => (
                        <TableRow key={request.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {request.tokenNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 200 }}
                            >
                              {request.title ||
                                request.description?.substring(0, 50) + "..."}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.priority}
                              color={getPriorityColor(request.priority)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.category}
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {request.requesterName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(request.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton
                                onClick={() => openRequestDetailDialog(request)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Approve">
                              <IconButton
                                color="success"
                                onClick={() => {
                                  setReviewDialog({
                                    open: true,
                                    request,
                                    decision: "approved",
                                    comments: "",
                                    priority: request.priority,
                                    loading: false,
                                  });
                                }}
                              >
                                <ThumbUpIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Review/Reject">
                              <IconButton
                                color="error"
                                onClick={() => openReviewDialog(request)}
                              >
                                <ThumbDownIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Assign Worker">
                              <IconButton
                                color="primary"
                                onClick={() => openAssignmentDialog(request)}
                              >
                                <AssignmentIndIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          )}

          {/* Assigned Tasks Tab */}
          {activeTab === 1 && (
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  Assigned Tasks ({assignedRequests.length})
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={refreshAllData}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>

              {assignedRequests.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <AssignmentIcon
                    sx={{ fontSize: 64, color: "info.light", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    No assigned tasks
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tasks will appear here once you assign them to workers.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Token #</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Assigned Worker</TableCell>
                        <TableCell>Scheduled Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignedRequests.map((request) => (
                        <TableRow key={request.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {request.tokenNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 200 }}
                            >
                              {request.title ||
                                request.description?.substring(0, 50) + "..."}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.priority}
                              color={getPriorityColor(request.priority)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.status}
                              color={getStatusColor(request.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {request.assignedWorkerName || "Not assigned"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {request.scheduledDate
                                ? formatDate(request.scheduledDate)
                                : "Not scheduled"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton
                                onClick={() => openRequestDetailDialog(request)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          )}

          {/* All Requests Tab */}
          {activeTab === 2 && (
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  All Requests in {hodProfile?.category} Category (
                  {allRequests.length})
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={refreshAllData}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>

              {allRequests.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <AssignmentIcon
                    sx={{ fontSize: 64, color: "primary.light", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    No requests found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No maintenance requests found for your category.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Token #</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Assigned To</TableCell>
                        <TableCell>Assigned Date</TableCell>
                        <TableCell>Requester</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allRequests.map((request) => (
                        <TableRow key={request.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {request.tokenNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 200 }}
                            >
                              {request.title ||
                                request.description?.substring(0, 50) + "..."}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.priority}
                              color={getPriorityColor(request.priority)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.status}
                              color={getStatusColor(request.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {request.assignedWorkerName ? (
                                <>
                                  {request.assignedWorkerName}
                                  {request.isExternalWorker && (
                                    <Chip
                                      label="External"
                                      size="small"
                                      color="info"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Not assigned
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {request.assignedAt
                                ? formatDate(request.assignedAt)
                                : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {request.requesterName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(request.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                request.status === "pending_hod_review"
                                  ? "Assign Worker"
                                  : "View Details"
                              }
                            >
                              <IconButton
                                onClick={() =>
                                  handleRequestAction(request, "allRequests")
                                }
                                color={
                                  request.status === "pending_hod_review"
                                    ? "primary"
                                    : "default"
                                }
                              >
                                {request.status === "pending_hod_review" ? (
                                  <AssignmentIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          )}

          {/* Team Management Tab */}
          {activeTab === 3 && (
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  Team Members ({managedWorkers.length})
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    console.log(
                      "[DEBUG] Refresh button clicked, hodProfile:",
                      hodProfile
                    );
                    console.log(
                      "[DEBUG] hodProfile.id:",
                      hodProfile?.id,
                      typeof hodProfile?.id
                    );
                    if (hodProfile?.id) {
                      loadManagedWorkers(hodProfile.id);
                    } else {
                      console.error(
                        "[ERROR] No valid HOD ID found for loading workers"
                      );
                      setError("Unable to load team members: HOD ID not found");
                    }
                  }}
                  disabled={loading}
                >
                  Refresh Team
                </Button>
              </Box>

              {managedWorkers.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <PersonIcon
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    No team members assigned
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Workers will appear here once they are assigned to your
                    category
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {managedWorkers.map((worker) => (
                    <Grid item xs={12} sm={6} md={4} key={worker.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 2,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <BuildIcon sx={{ mr: 1 }} />
                              <Typography variant="h6" noWrap>
                                {worker.name}
                              </Typography>
                            </Box>
                            <Tooltip title="Delete Worker">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() =>
                                  setDeleteWorkerDialog({
                                    open: true,
                                    worker: worker,
                                    loading: false,
                                  })
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            {worker.email}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            Specialization:{" "}
                            {worker.specialization?.join(", ") || "General"}
                          </Typography>
                          <Chip
                            label={worker.isAvailable ? "Available" : "Busy"}
                            color={worker.isAvailable ? "success" : "error"}
                            size="small"
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          )}
        </Card>

        {/* Request Review Dialog */}
        <Dialog
          open={reviewDialog.open}
          onClose={() => setReviewDialog((prev) => ({ ...prev, open: false }))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Review Request - {reviewDialog.request?.tokenNumber}
          </DialogTitle>
          <DialogContent>
            {reviewDialog.request && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Description:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {reviewDialog.request.description}
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Decision</InputLabel>
                  <Select
                    value={reviewDialog.decision}
                    onChange={(e) =>
                      setReviewDialog((prev) => ({
                        ...prev,
                        decision: e.target.value,
                      }))
                    }
                    label="Decision"
                  >
                    <MenuItem value="approved">Approve</MenuItem>
                    <MenuItem value="rejected">Reject</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Priority Level</InputLabel>
                  <Select
                    value={reviewDialog.priority}
                    onChange={(e) =>
                      setReviewDialog((prev) => ({
                        ...prev,
                        priority: e.target.value,
                      }))
                    }
                    label="Priority Level"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="HOD Comments"
                  multiline
                  rows={3}
                  fullWidth
                  value={reviewDialog.comments}
                  onChange={(e) =>
                    setReviewDialog((prev) => ({
                      ...prev,
                      comments: e.target.value,
                    }))
                  }
                  helperText="Provide reasoning for your decision"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setReviewDialog((prev) => ({ ...prev, open: false }))
              }
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestReview}
              disabled={
                !reviewDialog.decision ||
                !reviewDialog.comments ||
                reviewDialog.loading
              }
              variant="contained"
              startIcon={
                reviewDialog.loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <SaveIcon />
                )
              }
            >
              {reviewDialog.loading ? "Processing..." : "Submit Review"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Worker Assignment Dialog */}
        <Dialog
          open={assignmentDialog.open}
          onClose={() =>
            setAssignmentDialog((prev) => ({ ...prev, open: false }))
          }
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Assign Worker - {assignmentDialog.request?.tokenNumber}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              {/* Assignment Type Selection */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Assignment Type</InputLabel>
                <Select
                  value={assignmentDialog.assignmentType}
                  onChange={(e) =>
                    setAssignmentDialog((prev) => ({
                      ...prev,
                      assignmentType: e.target.value,
                      workerId: "", // Reset selections when changing type
                      externalWorkerName: "",
                    }))
                  }
                  label="Assignment Type"
                >
                  <MenuItem value="internal">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <PersonIcon sx={{ mr: 1 }} />
                      Internal Worker (From Team)
                    </Box>
                  </MenuItem>
                  <MenuItem value="external">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <BuildIcon sx={{ mr: 1 }} />
                      External/Temporary Worker
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Internal Worker Selection */}
              {assignmentDialog.assignmentType === "internal" && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Worker</InputLabel>
                  <Select
                    value={assignmentDialog.workerId}
                    onChange={(e) =>
                      setAssignmentDialog((prev) => ({
                        ...prev,
                        workerId: e.target.value,
                      }))
                    }
                    label="Select Worker"
                  >
                    {managedWorkers
                      .filter((w) => w.isAvailable)
                      .map((worker) => (
                        <MenuItem key={worker.id} value={worker.id}>
                          {worker.name} -{" "}
                          {worker.specialization?.join(", ") || "General"}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              {/* External Worker Details */}
              {assignmentDialog.assignmentType === "external" && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, color: "text.secondary" }}
                  >
                    External Worker Information
                  </Typography>

                  <TextField
                    label="Worker Name *"
                    fullWidth
                    required
                    sx={{ mb: 2 }}
                    value={assignmentDialog.externalWorkerName}
                    onChange={(e) =>
                      setAssignmentDialog((prev) => ({
                        ...prev,
                        externalWorkerName: e.target.value,
                      }))
                    }
                    helperText="Enter the name of the external worker"
                  />

                  <TextField
                    label="Phone Number"
                    fullWidth
                    sx={{ mb: 2 }}
                    value={assignmentDialog.externalWorkerPhone}
                    onChange={(e) =>
                      setAssignmentDialog((prev) => ({
                        ...prev,
                        externalWorkerPhone: e.target.value,
                      }))
                    }
                    helperText="Optional - Contact number for the worker"
                  />

                  <TextField
                    label="Email Address"
                    type="email"
                    fullWidth
                    sx={{ mb: 2 }}
                    value={assignmentDialog.externalWorkerEmail}
                    onChange={(e) =>
                      setAssignmentDialog((prev) => ({
                        ...prev,
                        externalWorkerEmail: e.target.value,
                      }))
                    }
                    helperText="Optional - Email address for notifications"
                  />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Worker Type</InputLabel>
                    <Select
                      value={assignmentDialog.externalWorkerType}
                      onChange={(e) =>
                        setAssignmentDialog((prev) => ({
                          ...prev,
                          externalWorkerType: e.target.value,
                        }))
                      }
                      label="Worker Type"
                    >
                      <MenuItem value="external">External Contractor</MenuItem>
                      <MenuItem value="temporary">Temporary Worker</MenuItem>
                      <MenuItem value="third_party">
                        Third Party Service
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Common Assignment Fields */}
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, mt: 3, color: "text.secondary" }}
              >
                Assignment Details
              </Typography>

              <TextField
                label="Scheduled Date"
                type="datetime-local"
                fullWidth
                sx={{ mb: 2 }}
                value={assignmentDialog.scheduledDate}
                onChange={(e) =>
                  setAssignmentDialog((prev) => ({
                    ...prev,
                    scheduledDate: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                helperText="When should the work be started"
              />

              <TextField
                label="Estimated Duration (hours)"
                type="number"
                fullWidth
                sx={{ mb: 2 }}
                value={assignmentDialog.estimatedDuration}
                onChange={(e) =>
                  setAssignmentDialog((prev) => ({
                    ...prev,
                    estimatedDuration: e.target.value,
                  }))
                }
                helperText="Expected time to complete the task"
              />

              <TextField
                label="HOD Notes"
                multiline
                rows={3}
                fullWidth
                value={assignmentDialog.notes}
                onChange={(e) =>
                  setAssignmentDialog((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                helperText="Special instructions for the assigned worker"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setAssignmentDialog((prev) => ({ ...prev, open: false }))
              }
            >
              Cancel
            </Button>
            <Button
              onClick={handleWorkerAssignment}
              disabled={
                (assignmentDialog.assignmentType === "internal" &&
                  !assignmentDialog.workerId) ||
                (assignmentDialog.assignmentType === "external" &&
                  !assignmentDialog.externalWorkerName) ||
                assignmentDialog.loading
              }
              variant="contained"
              startIcon={
                assignmentDialog.loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <AssignmentIndIcon />
                )
              }
            >
              {assignmentDialog.loading
                ? "Assigning..."
                : `Assign ${
                    assignmentDialog.assignmentType === "internal"
                      ? "Internal"
                      : "External"
                  } Worker`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Request Detail Dialog */}
        <Dialog
          open={requestDetailDialog.open}
          onClose={() =>
            setRequestDetailDialog((prev) => ({ ...prev, open: false }))
          }
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Request Details - {requestDetailDialog.request?.tokenNumber}
          </DialogTitle>
          <DialogContent>
            {requestDetailDialog.request && (
              <Box sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Category:
                    </Typography>
                    <Chip
                      label={requestDetailDialog.request.category}
                      color="primary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Priority:
                    </Typography>
                    <Chip
                      label={requestDetailDialog.request.priority}
                      color={getPriorityColor(
                        requestDetailDialog.request.priority
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Description:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {requestDetailDialog.request.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Requester:
                    </Typography>
                    <Typography variant="body2">
                      {requestDetailDialog.request.requesterName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Location:
                    </Typography>
                    <Typography variant="body2">
                      {requestDetailDialog.request.location}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Created:
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(requestDetailDialog.request.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Status:
                    </Typography>
                    <Chip
                      label={requestDetailDialog.request.status}
                      color={getStatusColor(requestDetailDialog.request.status)}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setRequestDetailDialog((prev) => ({ ...prev, open: false }))
              }
            >
              Close
            </Button>
            {requestDetailDialog.request?.status === "pending_hod_review" && (
              <>
                <Button
                  onClick={() => {
                    setRequestDetailDialog((prev) => ({
                      ...prev,
                      open: false,
                    }));
                    openAssignmentDialog(requestDetailDialog.request);
                  }}
                  variant="contained"
                  color="primary"
                  startIcon={<AssignmentIcon />}
                >
                  Assign Worker
                </Button>
                <Button
                  onClick={() => {
                    setRequestDetailDialog((prev) => ({
                      ...prev,
                      open: false,
                    }));
                    openReviewDialog(requestDetailDialog.request);
                  }}
                  variant="outlined"
                  startIcon={<EditIcon />}
                >
                  Review Only
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Delete Worker Confirmation Dialog */}
        <Dialog
          open={deleteWorkerDialog.open}
          onClose={() =>
            setDeleteWorkerDialog({ open: false, worker: null, loading: false })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <WarningIcon sx={{ mr: 1, color: "error.main" }} />
              Confirm Worker Deletion
            </Box>
          </DialogTitle>
          <DialogContent>
            {deleteWorkerDialog.worker && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to delete the following worker?
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    my: 2,
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {deleteWorkerDialog.worker.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Email: {deleteWorkerDialog.worker.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Specialization:{" "}
                    {deleteWorkerDialog.worker.specialization?.join(", ") ||
                      "General"}
                  </Typography>
                </Box>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Warning:</strong> This action cannot be undone. The
                    worker will be permanently removed from your team and all
                    associated assignments will be affected.
                  </Typography>
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setDeleteWorkerDialog({
                  open: false,
                  worker: null,
                  loading: false,
                })
              }
              disabled={deleteWorkerDialog.loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteWorker}
              variant="contained"
              color="error"
              disabled={deleteWorkerDialog.loading}
              startIcon={
                deleteWorkerDialog.loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {deleteWorkerDialog.loading ? "Deleting..." : "Delete Worker"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default HODDashboard;
